import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, LogOut, ArrowRight, ShieldCheck, Sparkles, Camera, Barcode, LineChart, Droplet, Flame, Activity, Flag } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";

// One-page, premium plan picker shown after account creation (or manually).
// Two clean tier cards, two optional add-ons as pill toggles, one live total,
// one CTA → Stripe. No missions, no HUDs, no walls of text.
// One-page, premium plan picker shown after account creation (or manually).
// Full plan + two add-ons (Tracker & TP). No Lite tier — coverage is now
// controlled by the "How will you use SPC?" wizard question.
const TIER = {
  lookup: "spc_monthly",
  name: "SPC Performance Nutrition",
  price: 1499,
  tagline: "Personalised weekly fuelling — every session fuelled, every race planned",
  features: [
    "Daily fuel targets from your training load — fuel every session",
    "Personalised meal plans + shopping lists with day-of-use badges",
    "In-session fuel & hydration pathways",
    "Race plans for every race you enter — unlimited, included",
    "Race week: T-3→T+1 carb-load timeline and race-morning meal timed to your gun",
    "Race day: 20-minute fuelling timetable, carbs/hr, sodium & electrolytes, what to carry, kit checklist",
    "Download any race plan as a PDF or copy it straight into TrainingPeaks",
    "Batch cook sheet — real grams/ml per meal so one pot feeds all week",
    "Wellness + adherence tracking with progress photos",
    "Weekly recap emails + AI coach chat",
  ],
};

const ADDONS_LEGACY_REMOVED = true; // kept as a no-op guard; addons are now rendered inline (Tracker as hero card, TP as row).

const TRACKER_FEATURES = [
  { icon: Barcode, text: "Log by search, barcode scan or AI food photo" },
  { icon: LineChart, text: "Segmented composition dial — Base · Carb Load · Race Fuel at a glance" },
  { icon: Droplet, text: "Weight · sleep · hydration · steps + progress photos" },
  { icon: Flame, text: "7-day trends, readiness insights + race-week prep cues" },
];

// Live-ish macro bars for the Tracker preview — animates on mount, dims when off.
function TrackerPreview({ armed }) {
  const bars = [
    { label: "Carbs",   color: "#FF9500", pct: 82 },
    { label: "Protein", color: "#4C9AFF", pct: 64 },
    { label: "Fat",     color: "#34C759", pct: 48 },
  ];
  return (
    <div className={`transition-opacity ${armed ? "opacity-100" : "opacity-40"}`} data-testid="tracker-preview">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-zinc-400">TODAY · LIVE MACROS</span>
        <span className="text-[9px] font-mono uppercase tracking-widest text-[#FF9500]">1,847 / 2,650 KCAL</span>
      </div>
      <div className="space-y-2.5">
        {bars.map((b, i) => (
          <div key={b.label}>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-zinc-400 mb-1 font-mono">
              <span>{b.label}</span>
              <span>{b.pct}%</span>
            </div>
            <div className="h-1.5 w-full bg-white/8 overflow-hidden">
              <motion.div
                className="h-full"
                style={{ backgroundColor: b.color, boxShadow: `0 0 12px ${b.color}55` }}
                initial={{ width: 0 }}
                animate={{ width: armed ? `${b.pct}%` : `${b.pct * 0.3}%` }}
                transition={{ duration: 0.9, delay: 0.1 + i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

// One-off Race Pack SKUs — entirely separate to the monthly subscription:
// single payment, no trial, no renewal, race fuelling only.
const RACE_PACKS = [
  { lookup: "race_pack_oneoff", price: 999, name: "Race Pack",
    blurb: "One race — race week + race day. Credit valid 12 months." },
  { lookup: "race_pack_annual", price: 1999, name: "Race Season",
    blurb: "Unlimited race plans for 12 months." },
];

export default function Subscribe() {
  const { isAuthed, hasAccess, logout, user, planTier, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Tracker on by default (92% add it, no extra info needed).
  // TP off by default (requires an email — no dead-end on the CTA).
  const [addons, setAddons] = useState({ tracker: true, tp: false });
  const [tpEmail, setTpEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [racePackLoading, setRacePackLoading] = useState("");
  const [stripeMode, setStripeMode] = useState("live");

  useEffect(() => {
    api.get("/config").then(({ data }) => setStripeMode(data.stripeMode || "live")).catch(() => {});
  }, []);

  const tier = TIER;
  const total = tier.price + (addons.tracker ? 499 : 0) + (addons.tp ? 999 : 0);
  const isTestMode = stripeMode === "test";
  const hasLiveSub = ["trialing", "active"].includes(user?.subscription?.status);
  const isCurrent = hasLiveSub && user?.subscription?.plan === tier.lookup;
  const tppActive = ["pending", "active", "cancelling"].includes(user?.tpPremium?.status);
  const addingTp = addons.tp && !tppActive;
  const tpValid = tpEmail.includes("@") && tpEmail.includes(".");

  const buyRacePack = async (lookup) => {
    if (!isAuthed) {
      try {
        sessionStorage.setItem("spc_pending_plan", JSON.stringify({ lookup_key: lookup }));
      } catch {}
      navigate("/login", { state: { mode: "register" } });
      return;
    }
    setRacePackLoading(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup, origin_url: window.location.origin });
      if (data?.error || !data?.checkout_url) {
        toast.error(data?.error || "Could not start checkout. Please try again.");
        setRacePackLoading("");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err, "Could not start checkout. Please try again."));
      setRacePackLoading("");
    }
  };

  const start = async () => {    if (addons.tp && !tppActive && !tpValid) {
      toast.error("Enter your TrainingPeaks account email to add Premium");
      return;
    }
    if (!isAuthed) {
      // Save selection so /login can auto-continue to Stripe after register.
      try {
        sessionStorage.setItem("spc_pending_plan", JSON.stringify({
          lookup_key: tier.lookup,
          add_tracker: addons.tracker,
          add_tp_premium: addons.tp,
          tp_email: tpEmail.trim() || null,
        }));
      } catch {}
      navigate("/login", { state: { mode: "register" } });
      return;
    }
    if (isCurrent && !addingTp) return;
    setLoading(true);
    try {
      if (hasLiveSub) {
        if (addingTp) await api.post("/tp-premium/subscribe", { tp_email: tpEmail.trim() });
        await refreshUser?.();
        toast.success(addingTp ? "TrainingPeaks Premium added — we'll set you up within 24h." : "Plan updated.");
        navigate("/");
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup,
        add_tracker: addons.tracker,
        add_tp_premium: addons.tp,
        tp_email: addons.tp ? tpEmail.trim() : null,
        origin_url: window.location.origin,
      });
      if (data?.error || !data?.checkout_url) {
        toast.error(data?.error || "Could not start checkout. Please try again.");
        setLoading(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err, "Could not complete that. Please try again."));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans pb-32 sm:pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="hidden sm:inline font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Performance</span> Nutrition
            </span>
          </Link>
          {isAuthed ? (
            <button
              data-testid="subscribe-logout-btn"
              onClick={logout}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          ) : (
            <Link to="/login" data-testid="subscribe-signin" className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors">Sign in</Link>
          )}
        </div>
      </header>

      <main data-testid="subscribe-page" className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        {/* Hero title */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-bold">
            {user?.name ? `Hi ${user.name.split(" ")[0]} —` : "Welcome —"} pick your plan
          </p>
          <h1 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter font-black leading-[0.95] mt-3">
            Fuel every session.<br /><span className="text-[#E2E30A]">Plan every race.</span>
          </h1>
          <p className="text-zinc-400 mt-3 max-w-lg text-sm sm:text-base">
            Weekly fuelling around your real training, and a full race week + race day plan for
            every event you enter. 7-day trial — £0 today, first charge day 8, cancel anytime.
          </p>
        </motion.div>

        {/* ─── SINGLE TIER CARD ─── */}
        <div className="mt-8 sm:mt-10">
          <div
            data-testid="subscribe-tier-full"
            className="relative flex flex-col p-6 sm:p-8 bg-gradient-to-br from-[#1a1a08] via-[#141406] to-[#0f0f04]"
            style={{
              borderWidth: "2px",
              borderStyle: "solid",
              borderColor: "#E2E30A",
              boxShadow: "0 25px 70px -30px rgba(226,227,10,0.45)",
            }}
          >
            <span className="absolute -top-3 left-6 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-black bg-[#E2E30A] px-2 py-1">
              <Sparkles className="h-3 w-3" /> 7 days free · £0 today
            </span>

            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E2E30A]">Your plan</p>
                <p className="text-lg text-white font-bold mt-1">{TIER.name}</p>
                <p className="text-sm text-zinc-300 mt-1 max-w-[280px]">{TIER.tagline}</p>
              </div>
              <div className="text-right shrink-0">
                <span className="font-display text-4xl sm:text-5xl leading-none tracking-tighter font-black tabular-nums text-white">
                  {money(TIER.price)}
                </span>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">/ month</p>
              </div>
            </div>

            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {TIER.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[13px] text-zinc-200 leading-relaxed">
                  <Check className="h-4 w-4 shrink-0 mt-0.5 text-[#E2E30A]" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ─── ADD-ONS ─── */}
        <div className="mt-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold">Add-ons · make it complete</p>

          {/* Food Tracker — hero feature card matching tier tiles */}
          <motion.button
            type="button"
            data-testid="addon-toggle-tracker"
            onClick={() => setAddons((s) => ({ ...s, tracker: !s.tracker }))}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className={`group relative w-full text-left flex flex-col pt-8 pb-6 sm:pt-9 sm:pb-7 px-6 sm:px-7 mt-6 transition-all overflow-hidden ${
              addons.tracker
                ? "bg-gradient-to-br from-[#1a0f00] via-[#140a00] to-[#0f0700]"
                : "bg-[#111] hover:bg-[#161616]"
            }`}
            style={{
              borderWidth: addons.tracker ? "2px" : "1px",
              borderStyle: "solid",
              borderColor: addons.tracker ? "#FF9500" : "rgba(255,255,255,0.14)",
              boxShadow: addons.tracker ? "0 25px 70px -30px rgba(255,149,0,0.45)" : "none",
            }}
          >
            {/* Ribbon — inside the card so it never gets clipped */}
            <span className="absolute top-0 left-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-black bg-[#FF9500] px-2.5 py-1">
              <Sparkles className="h-3 w-3" /> 92% of athletes add this
            </span>

            {/* Ambient orange glow when armed */}
            {addons.tracker && (
              <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(255,149,0,0.22) 0%, transparent 70%)" }} />
            )}

            <div className="relative flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <span className="grid place-items-center h-11 w-11 shrink-0 bg-[#FF9500]/12 border border-[#FF9500]/40">
                  <Camera className="h-5 w-5 text-[#FF9500]" strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF9500]">Food Tracker</p>
                  <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tighter font-black leading-none mt-1.5">
                    Log every bite<br className="hidden sm:inline" /> <span className="sm:hidden">— </span>hit every target.
                  </h3>
                </div>
              </div>
              <span className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 mt-1 ${addons.tracker ? "border-[#FF9500]" : "border-white/25"}`}>
                {addons.tracker && <span className="h-2 w-2 rounded-full bg-[#FF9500]" />}
              </span>
            </div>

            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display text-5xl sm:text-6xl leading-none tracking-tighter font-black tabular-nums text-white">
                +{money(499)}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1.5">/ month</span>
            </div>

            {/* Split: features left, macro preview right (stack on mobile) */}
            <div className="mt-6 grid sm:grid-cols-[1fr_1fr] gap-6 items-start">
              <ul className="space-y-2.5">
                {TRACKER_FEATURES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <li key={f.text} className="flex items-start gap-2.5 text-[13px] text-zinc-200 leading-relaxed">
                      <Icon className="h-4 w-4 shrink-0 mt-0.5 text-[#FF9500]" strokeWidth={2.5} />
                      <span>{f.text}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="bg-black/50 border border-white/8 p-4">
                <TrackerPreview armed={addons.tracker} />
              </div>
            </div>

            {/* Slam-toggle strip at the bottom */}
            <div
              className="mt-6 flex items-center justify-between border-t pt-4"
              style={{ borderColor: addons.tracker ? "rgba(255,149,0,0.25)" : "rgba(255,255,255,0.08)" }}
            >
              <span className="text-[10px] uppercase tracking-widest font-mono font-bold" style={{ color: addons.tracker ? "#FF9500" : "rgba(255,255,255,0.4)" }}>
                {addons.tracker ? "Added · included in your total" : "Tap to add · +£4.99/mo"}
              </span>
              <span
                className="relative h-7 w-12 rounded-full transition-colors"
                style={{ background: addons.tracker ? "#FF9500" : "rgba(255,255,255,0.15)" }}
              >
                <motion.span
                  layout
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className="absolute top-1 h-5 w-5 rounded-full bg-white shadow"
                  style={{ left: addons.tracker ? "24px" : "2px" }}
                />
              </span>
            </div>
          </motion.button>

          {/* TrainingPeaks Premium — compact row */}
          <div className="mt-3">
            <button
              type="button"
              data-testid="addon-toggle-tp"
              onClick={() => {
                if (tppActive) return;
                setAddons((s) => ({ ...s, tp: !s.tp }));
              }}
              disabled={tppActive}
              className={`w-full text-left flex items-center gap-4 p-4 sm:p-5 transition-colors ${
                addons.tp || tppActive ? "bg-white/[0.03]" : "bg-white/[0.015] hover:bg-white/[0.03]"
              }`}
              style={{
                borderWidth: addons.tp || tppActive ? "2px" : "1px",
                borderStyle: "solid",
                borderColor: addons.tp || tppActive ? "#4C9AFF" : "rgba(255,255,255,0.12)",
              }}
            >
              <span
                className="relative h-6 w-11 shrink-0 rounded-full transition-colors"
                style={{ background: addons.tp || tppActive ? "#4C9AFF" : "rgba(255,255,255,0.15)" }}
              >
                <motion.span
                  layout
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow"
                  style={{ left: addons.tp || tppActive ? "22px" : "2px" }}
                />
              </span>

              <span className="grid place-items-center h-9 w-9 shrink-0 bg-[#4C9AFF]/12 border border-[#4C9AFF]/40">
                <Activity className="h-4 w-4 text-[#4C9AFF]" strokeWidth={2.5} />
              </span>

              <div className="flex-1 min-w-0">
                <p className="font-display text-lg sm:text-xl uppercase tracking-tight font-bold leading-none">TrainingPeaks Premium</p>
                <p className="text-[12px] sm:text-[13px] text-zinc-400 mt-1">Live sync + burned-kcal fuel credits</p>
              </div>

              <div className="text-right shrink-0">
                <p className="font-display text-xl sm:text-2xl leading-none tracking-tighter tabular-nums text-[#4C9AFF]">
                  +{money(999)}
                </p>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                  {tppActive ? "Already active" : "/ month"}
                </p>
              </div>
            </button>

            {/* TP email input reveal */}
            <AnimatePresence>
              {addons.tp && !tppActive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2.5 border border-[#4C9AFF]/30 bg-[#4C9AFF]/[0.04] px-4 py-3">
                    <label className="text-[10px] uppercase tracking-wider text-[#4C9AFF] font-bold">Your TrainingPeaks email</label>
                    <input
                      data-testid="subscribe-tp-email"
                      type="email"
                      value={tpEmail}
                      onChange={(e) => setTpEmail(e.target.value)}
                      placeholder="you@trainingpeaks.com"
                      className="mt-1.5 w-full h-10 bg-black/50 border border-white/12 px-3 text-sm text-white outline-none focus:border-[#4C9AFF] font-mono"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1.5">We link Premium to this TP account within 24h.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ─── ONE-OFF RACE PACK (separate to any subscription) ─── */}
        {!hasLiveSub && (
          <div className="mt-14 border-t border-white/10 pt-10" data-testid="race-pack-block">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">
              One-off · no subscription
            </p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter font-black mt-2">
              Just need a race?
            </h2>
            <p className="text-sm text-zinc-300 mt-2 max-w-lg">
              Race week + race day fuelling only — carb load, race-morning meal, the 20-minute
              in-race timetable, sodium and what to carry, as a PDF you can take with you.
              A single payment. No subscription, nothing to cancel.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {RACE_PACKS.map((p) => (
                <button
                  key={p.lookup}
                  type="button"
                  data-testid={`subscribe-${p.lookup}`}
                  onClick={() => buyRacePack(p.lookup)}
                  disabled={!!racePackLoading}
                  className="text-left border border-white/14 hover:border-[#E2E30A]/60 bg-white/[0.02] hover:bg-[#E2E30A]/[0.04] p-5 transition-colors disabled:opacity-60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-xl uppercase tracking-tight">{p.name}</p>
                      <p className="text-xs text-zinc-400 mt-1 max-w-[220px]">{p.blurb}</p>
                    </div>
                    <Flag className="h-5 w-5 text-[#E2E30A] shrink-0" />
                  </div>
                  <p className="font-display text-4xl font-black tabular-nums mt-4">
                    {money(p.price)}
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">one-off</span>
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#E2E30A] font-bold">
                    {racePackLoading === p.lookup ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    Buy {p.name} <ArrowRight className="h-3 w-3" />
                  </span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">
              Charged once — this is not part of the monthly total above.
            </p>
          </div>
        )}

        {/* ─── FINE PRINT ─── */}
        <p className="text-[11px] text-zinc-500 mt-8 max-w-2xl leading-relaxed">
          Prices shown are the total charged — <span className="text-white font-semibold">no VAT added</span>.
          {isTestMode && <span className="text-amber-400"> · Test mode · card 4242 4242 4242 4242</span>}
        </p>
      </main>

      {/* ─── STICKY CTA BAR ─── */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t-2 border-[#E2E30A]/25">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Total · monthly</p>
            <AnimatePresence mode="popLayout">
              <motion.p
                key={total}
                initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.22 }}
                data-testid="subscribe-total"
                className="font-display text-3xl sm:text-4xl leading-none tracking-tighter font-black tabular-nums mt-1"
              >
                {money(total)}<span className="text-[11px] uppercase tracking-widest text-zinc-500 ml-1.5">/ mo</span>
              </motion.p>
            </AnimatePresence>
            <p className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400 mt-1.5">
              <ShieldCheck className="h-3 w-3" /> £0 today · first charge day 8
            </p>
          </div>

          <button
            type="button"
            data-testid="subscribe-start-btn"
            onClick={start}
            disabled={loading || (isCurrent && !addingTp)}
            className="group inline-flex items-center gap-2 sm:gap-3 bg-[#E2E30A] hover:bg-white disabled:bg-white/8 disabled:text-white/40 text-black font-display font-black uppercase tracking-wider text-sm sm:text-base px-5 sm:px-8 py-4 sm:py-5 transition-colors shrink-0"
            style={{ boxShadow: (loading || (isCurrent && !addingTp)) ? "none" : "0 18px 45px -18px rgba(226,227,10,0.6)" }}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (isCurrent && !addingTp) ? (
              <span>Current plan</span>
            ) : !isAuthed ? (
              <>
                <span>Create account &amp; start</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </>
            ) : hasLiveSub ? (
              <>
                <span>Switch plan</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </>
            ) : (
              <>
                <span>Start free trial</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
